import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { RotateCw } from 'lucide-react';
import { isResendOffered, type ResendVia } from '@atink/domain';
import { hasMoneyIn, resendExpiredLink, type ResendPrefill } from '@/domain/depositFlow';
import { intentFailureCopy } from '@/api/intents';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { customerName } from '@/utils/format';
import DepositLinkModal from '@/components/DepositLinkModal';
import type { Deposit, LinkSenderRole } from '@/domain/types';

/**
 * The ONE "Resend link" for an expired, unpaid deposit link (W5b #2,
 * tv-2na.8.20 / 8.19). Mounted wherever a user looks at an expired link: the
 * deposit ledger, the conversation timeline, the queue work window header, the
 * Ops "Links >48h" body, Sweeper Missed deposits and (Track C) the customer
 * record. Every mount shares the same rule and the same honest outcome:
 *
 * - offered only on a genuinely unpaid, expired link, for 7 days after expiry
 *   (Q2) — `via="sweeper"` lifts the window for the Sweeper's surfaces; never on
 *   part-paid / paid / used / rolled-over money, never to the artist seats;
 * - same slot still free -> one server intent; the "Link resent" toast shows
 *   only once it landed;
 * - past / taken slot -> "Pick a new date" (Q1): DepositLinkModal opens with the
 *   old artist, tier and amount; the old row stays Expired;
 * - `sentByRole`: the resending surface's role, so the resender owns the
 *   follow-up (Q3). Omitted = the session persona.
 */
export interface ResendLinkActionProps {
  deposit: Deposit;
  sentByRole?: LinkSenderRole;
  via?: ResendVia;
  /** 'pill' (ledger / lists), 'ghost' (timeline card), 'header' (work window). */
  variant?: 'pill' | 'ghost' | 'header';
  label?: string;
  /** Defaults to `deposit-resend-<depositId>`. */
  testId?: string;
  /** After a landed resend (not after "Pick a new date" opens). */
  onResent?: (freshDepositId: string) => void;
}

const NOT_RESENDABLE_COPY: Record<string, string> = {
  'money-in': 'Money is already in on this deposit, so there is no link to resend.',
  'moved-on': 'This deposit has been paid, used or moved on.',
  'already-resent': 'A fresh link was already sent for this one.',
  'not-expired': 'The link is still live. The client can still pay it.',
  'window-closed': 'It expired more than 7 days ago. The Sweeper can pick it up from the Expired rung.',
  'pay-at-studio': 'This is a pay-at-studio hold. No link was sent.',
};

const VARIANT_CLS: Record<NonNullable<ResendLinkActionProps['variant']>, string> = {
  pill: 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest border border-gold-dim/40 text-gold-bright hover:border-gold-bright hover:bg-gold/10 transition',
  ghost: 'btn-ghost text-[11px]',
  header: 'btn-ghost text-xs border-gold-bright/60 text-gold-bright',
};

export default function ResendLinkAction({
  deposit,
  sentByRole,
  via,
  variant = 'pill',
  label = 'Resend link',
  testId,
  onResent,
}: ResendLinkActionProps) {
  const persona = useSessionStore((s) => s.persona);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  const customer = useBrainStore((s) => s.customers[deposit.customerId]);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const setWorkContext = useUIStore((s) => s.setWorkContext);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState<ResendPrefill | null>(null);

  // "Pick a new date" owns the work context while it is open; clear it on close
  // (or if this action unmounts with the modal still open).
  useEffect(() => {
    if (!pick) return undefined;
    setWorkContext({ kind: 'resend', customerId: pick.customerId, ...(sentByRole ? { sentByRole } : {}) });
    return () => {
      const ctx = useUIStore.getState().workContext;
      if (ctx?.kind === 'resend' && ctx.customerId === pick.customerId) setWorkContext(null);
    };
  }, [pick, sentByRole, setWorkContext]);

  const readOnlySeat = persona === 'artist' || persona === 'apprentice';
  const offered =
    !readOnlySeat &&
    isResendOffered(deposit, clockIso, via ? { via } : {}) &&
    !hasMoneyIn(deposit, topupPlans);
  if (!offered && !pick) return null;

  const who = customer ? customerName(customer) : 'Awaiting client';

  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const out = await resendExpiredLink({
      depositId: deposit.id,
      ...(sentByRole ? { sentByRole } : {}),
      ...(via ? { via } : {}),
    });
    setBusy(false);
    switch (out.outcome) {
      case 'ok':
        // On Instagram the delivery pushes its own toast (tv-isee).
        if (out.route && out.route !== 'local') {
          onResent?.(out.freshDepositId);
          return;
        }
        pushNotification({
          intensity: 'info',
          title: 'Link resent',
          body: `${who} · a fresh link went out. The old one stays on file as resent.`,
          ttlMs: 4000,
          ...(customer ? { customerId: customer.id } : {}),
        });
        onResent?.(out.freshDepositId);
        return;
      case 'needs-new-date':
        if (!customer) {
          pushNotification({
            intensity: 'high',
            title: 'Pick a new date',
            body:
              out.reason === 'no-date'
                ? 'This link never had a date. Send a fresh self-book link instead.'
                : 'That date has gone. Send a fresh self-book link instead.',
            ttlMs: 5000,
          });
          return;
        }
        setPick(out.prefill);
        return;
      case 'already-resent':
        pushNotification({ intensity: 'info', title: 'Already resent', body: NOT_RESENDABLE_COPY['already-resent']!, ttlMs: 4000 });
        return;
      case 'not-resendable':
        pushNotification({
          intensity: 'info',
          title: "Can't resend this link",
          body: NOT_RESENDABLE_COPY[out.reason] ?? 'This link cannot be re-sent.',
          ttlMs: 5000,
        });
        return;
      case 'failed': {
        const copy = intentFailureCopy(out.failure);
        pushNotification({
          intensity: 'critical',
          title: "Couldn't resend the link",
          body: copy.retry ? "We couldn't reach the server, so no link went out. Try again." : copy.body,
          ttlMs: 8000,
        });
      }
    }
  };

  return (
    <>
      {offered ? (
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          className={clsx(VARIANT_CLS[variant], 'disabled:opacity-50 disabled:cursor-wait')}
          data-testid={testId ?? `deposit-resend-${deposit.id}`}
          title="Resend the deposit link"
        >
          <RotateCw size={variant === 'header' ? 12 : 10} /> {busy ? 'Resending…' : label}
        </button>
      ) : null}
      {pick && customer ? (
        // Clicks inside the modal must not reach a clickable host row (the ledger
        // row navigates to the customer).
        <span onClick={(e) => e.stopPropagation()} data-testid={`resend-pick-date-${deposit.id}`}>
          <DepositLinkModal
            open
            customer={customer}
            onClose={() => setPick(null)}
            heading="Pick a new date"
            resendOfDepositId={deposit.id}
            {...(pick.artistId ? { initialArtistId: pick.artistId } : {})}
            initialSession={pick.sessionLength}
            initialDepositAud={pick.amountAud}
            {...(pick.pieceName ? { initialPieceName: pick.pieceName } : {})}
          />
        </span>
      ) : null}
    </>
  );
}
